/*
 * Узоры фона ленты сообщений.
 *
 * Каждый мотив собирается в адрес данных с нужным цветом: тема задаёт оттенок,
 * а не выбирает из двух заранее закрашенных картинок. Переменные CSS внутрь
 * data-адреса не проникают, поэтому цвет приходится вшивать в саму разметку —
 * зато делает это одна функция, а не два десятка строк в таблице стилей.
 */

export type PatternId =
	'classic' | 'cosmos' | 'neon' | 'forest' | 'sunset' | 'sea' | 'graphite' | 'none';

/** Геометрия мотивов. Сетка 120×120, обводка задаётся снаружи. */
const SHAPES: Record<Exclude<PatternId, 'none'>, string> = {
	// Кольца, искры и точки — исходный мотив.
	classic:
		"<circle cx='22' cy='26' r='7'/><circle cx='92' cy='96' r='5'/><path d='M70 34v10M65 39h10'/><path d='M14 78l10 6 9-5'/><path d='M84 12l4 4-4 4-4-4z'/><path d='M100 62l6 4-6 4'/>",

	// Звёзды разной величины и дуга орбиты.
	cosmos:
		"<path d='M24 20l3 7 7 3-7 3-3 7-3-7-7-3 7-3z'/><path d='M92 76l2 5 5 2-5 2-2 5-2-5-5-2 5-2z'/><circle cx='84' cy='24' r='9'/><path d='M72 30a14 14 0 0 0 24 12'/><path d='M18 92l2 4 4 2-4 2-2 4-2-4-4-2 4-2z'/><circle cx='56' cy='58' r='3'/>",

	// Сетка и ромбы — техничный мотив.
	neon: "<path d='M0 40h120M0 80h120M40 0v120M80 0v120'/><path d='M20 20l6 6-6 6-6-6z'/><path d='M100 60l6 6-6 6-6-6z'/><path d='M60 100l6 6-6 6-6-6z'/>",

	// Листья и ветви.
	forest:
		"<path d='M22 34c0-9 7-16 16-16 0 9-7 16-16 16z'/><path d='M22 34l16-16'/><path d='M86 88c0-9 7-16 16-16 0 9-7 16-16 16z'/><path d='M86 88l16-16'/><path d='M56 62c6-6 6-16 0-22'/><path d='M56 62c-6-6-6-16 0-22'/>",

	// Волны и солнечные дуги.
	sunset:
		"<path d='M0 30c12-10 24 10 36 0s24 10 36 0 24 10 36 0 24 10 12 0'/><path d='M0 90c12-10 24 10 36 0s24 10 36 0 24 10 36 0'/><circle cx='96' cy='56' r='8'/><path d='M96 42v-6M96 76v-6M110 56h6M76 56h6'/>",

	// Пузыри разной величины.
	sea: "<circle cx='26' cy='30' r='9'/><circle cx='44' cy='18' r='4'/><circle cx='90' cy='84' r='7'/><circle cx='104' cy='70' r='3'/><circle cx='64' cy='60' r='5'/><circle cx='18' cy='96' r='4'/><circle cx='100' cy='28' r='5'/>",

	// Ровная сетка точек — спокойный мотив без образов.
	graphite:
		"<circle cx='20' cy='20' r='2'/><circle cx='60' cy='20' r='2'/><circle cx='100' cy='20' r='2'/><circle cx='40' cy='60' r='2'/><circle cx='80' cy='60' r='2'/><circle cx='20' cy='100' r='2'/><circle cx='60' cy='100' r='2'/><circle cx='100' cy='100' r='2'/>"
};

/**
 * Собрать адрес данных с узором.
 *
 * @param id мотив; `none` даёт `none` — фон остаётся без узора
 * @param color цвет обводки в шестнадцатеричном виде, например `#ffffff`
 * @param opacity прозрачность обводки, 0…1
 */
export function patternImage(id: PatternId, color: string, opacity = 0.07): string {
	if (id === 'none') return 'none';

	// Решётку в цвете нужно закодировать: в адресе данных она означает якорь.
	const stroke = color.replace('#', '%23');

	const svg =
		`<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120' ` +
		`fill='none' stroke='${stroke}' stroke-opacity='${opacity}' stroke-width='1.5' ` +
		`stroke-linecap='round' stroke-linejoin='round'>${SHAPES[id]}</svg>`;

	return `url("data:image/svg+xml,${svg.replace(/</g, '%3C').replace(/>/g, '%3E')}")`;
}
